import React, { useEffect, useState, useRef } from "react";
import { apiFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatearMoneda } from "../lib/formato";
import { reproducirSonidoPagoBancario, reproducirVozPagoBancario } from "../lib/sonido";
import ModalVincularPagoBancario from "./ModalVincularPagoBancario";
import type { BancoNotificacion } from "@tentacion/shared";

interface PagoBancarioItem {
  id: string;
  banco: BancoNotificacion;
  monto: number | string;
  remitente?: string | null;
  referencia?: string | null;
  recibidoEn: string;
  estado: string;
}

const BADGES_BANCO: Record<BancoNotificacion, { bg: string; label: string; icono: string }> = {
  NEQUI: {
    bg: "bg-purple-500 text-white",
    label: "Nequi",
    icono: "🟣",
  },
  BANCOLOMBIA: {
    bg: "bg-amber-400 text-slate-950 font-bold",
    label: "Bancolombia",
    icono: "🟡",
  },
  DAVIPLATA: {
    bg: "bg-rose-600 text-white",
    label: "DaviPlata",
    icono: "🔴",
  },
  OTRO: {
    bg: "bg-blue-600 text-white",
    label: "Banco",
    icono: "🏦",
  },
};

export default function NotificadorPagosBancarios() {
  const { usuario } = useAuth();

  // IMPORTANTE: todos los hooks deben declararse ANTES de cualquier return
  // condicional (Rules of Hooks). El early-return estaba después de useAuth
  // pero antes de los demás hooks — esto causaba crashes silenciosos en React.
  const [pagosPendientes, setPagosPendientes] = useState<PagoBancarioItem[]>([]);
  const [pagoDestacado, setPagoDestacado] = useState<PagoBancarioItem | null>(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const idsVistos = useRef<Set<string>>(new Set());
  const esPrimeraCarga = useRef(true);

  // Si es MESERA o no tiene notificarPagosBancarios (y no es ADMIN), nunca consultar ni sonar nada
  const habilitado = Boolean(
    usuario && usuario.rol !== "MESERA" && (usuario.notificarPagosBancarios || usuario.rol === "ADMIN"),
  );

  const consultarPagos = async () => {
    if (!habilitado) return;
    try {
      const res = await apiFetch<PagoBancarioItem[]>("/pagos-bancarios?estado=PENDIENTE");
      if (Array.isArray(res)) {
        setPagosPendientes(res);

        // Detectar si hay pagos totalmente nuevos
        const nuevos = res.filter((p) => !idsVistos.current.has(p.id));

        if (nuevos.length > 0) {
          // Registrar los nuevos IDs vistos
          nuevos.forEach((p) => idsVistos.current.add(p.id));

          // Si no es la primera carga limpia, reproducir el timbre sonoro y la voz alta
          if (!esPrimeraCarga.current && nuevos[0]) {
            reproducirSonidoPagoBancario();
            reproducirVozPagoBancario(nuevos[0].monto, nuevos[0].banco, nuevos[0].remitente);
            setPagoDestacado(nuevos[0]);
          }
        }

        esPrimeraCarga.current = false;
      }
    } catch {
      // Ignorar errores de red temporales
    }
  };

  useEffect(() => {
    if (!habilitado) return;
    consultarPagos();
    const timer = setInterval(consultarPagos, 10000); // Polling cada 10 segundos
    return () => clearInterval(timer);
  }, [habilitado]);

  // El aviso emergente se cierra solo al minuto — coincide con el backend,
  // que auto-confirma el pago pendiente si nadie lo hizo a mano antes de eso.
  useEffect(() => {
    if (!pagoDestacado) return;
    const t = setTimeout(() => setPagoDestacado(null), 60_000);
    return () => clearTimeout(t);
  }, [pagoDestacado]);

  // No renderizar nada si el usuario no está habilitado para ver pagos
  if (!habilitado) return null;

  const configBanco = pagoDestacado ? BADGES_BANCO[pagoDestacado.banco] ?? BADGES_BANCO.OTRO : null;

  return (
    <>
      {/* ALERTA FLOTANTE EMERGENTE */}
      {pagoDestacado && configBanco && (
        <div className="fixed top-5 right-5 z-[100] w-full max-w-sm overflow-hidden rounded-3xl bg-superficie border-2 border-emerald-500/40 p-4 shadow-2xl backdrop-blur-xl animate-in slide-in-from-top-4 duration-300">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/15 text-2xl shadow-inner border border-emerald-500/30">
              💰
            </div>

            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${configBanco.bg}`}>
                  {configBanco.icono} {configBanco.label}
                </span>

                <div className="flex items-center gap-1">
                  {/* BOTÓN PARA REPETIR VOZ */}
                  <button
                    type="button"
                    title="Escuchar pago por voz alta"
                    onClick={() =>
                      reproducirVozPagoBancario(
                        pagoDestacado.monto,
                        pagoDestacado.banco,
                        pagoDestacado.remitente,
                      )
                    }
                    className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 text-xs transition-colors"
                  >
                    🗣️
                  </button>

                  <button
                    type="button"
                    onClick={() => setPagoDestacado(null)}
                    className="flex h-7 w-7 items-center justify-center text-texto-secundario hover:text-texto text-xs font-bold"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="font-display text-lg font-black text-emerald-600">
                {formatearMoneda(Number(pagoDestacado.monto))}
              </div>

              {pagoDestacado.remitente && (
                <p className="text-xs font-semibold text-texto truncate">
                  De: {pagoDestacado.remitente}
                </p>
              )}

              {pagoDestacado.referencia && (
                <p className="text-[11px] text-texto-secundario">
                  Ref: <code className="font-mono text-texto">{pagoDestacado.referencia}</code>
                </p>
              )}

              <div className="pt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setPagoDestacado(null);
                    setModalAbierto(true);
                  }}
                  className="w-full py-2 px-3 text-xs font-black text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-xl shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
                >
                  Ver Transferencias ({pagosPendientes.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* BOTÓN FLOTANTE INDICADOR DE TRANSFERENCIAS PENDIENTES EN POS */}
      {pagosPendientes.length > 0 && !pagoDestacado && (
        <button
          type="button"
          onClick={() => setModalAbierto(true)}
          className="fixed bottom-6 right-6 z-[90] flex items-center gap-2.5 rounded-full bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 px-4 py-3 text-xs font-black text-white shadow-xl shadow-emerald-600/30 transition-all hover:scale-105 active:scale-95 border border-emerald-400/30 animate-bounce"
        >
          <span className="text-base">💰</span>
          <span>{pagosPendientes.length} Pago{pagosPendientes.length > 1 ? "s" : ""} Recibido{pagosPendientes.length > 1 ? "s" : ""}</span>
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-emerald-700 text-[10px] font-black">
            {pagosPendientes.length}
          </span>
        </button>
      )}

      {/* MODAL PARA VINCULAR PAGO BANCARIO */}
      <ModalVincularPagoBancario
        abierto={modalAbierto}
        onCerrar={() => {
          setModalAbierto(false);
          consultarPagos();
        }}
        onAsociado={() => consultarPagos()}
      />
    </>
  );
}
